import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle, Smartphone } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface VersionData {
  x: number;
  y: number;
  z: number;
  storeUrl: string;
  forceUpdate: boolean;
}

const VersionManager: React.FC = () => {
  const [platform, setPlatform] = useState<'android' | 'ios'>('android');
  
  const [versionData, setVersionData] = useState<VersionData>({
    x: 1,
    y: 0,
    z: 0,
    storeUrl: 'https://play.google.com/store/apps/details?id=com.kupigames.tower',
    forceUpdate: false
  });

  const [originalVersion, setOriginalVersion] = useState<VersionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchCurrentVersion();
  }, [platform]);

  const fetchCurrentVersion = async () => {
    try {
      setLoading(true);
      const docName = platform === 'ios' ? 'currentVersion_ios' : 'currentVersion';
      const docRef = doc(db, 'settings', docName);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as VersionData;
        setVersionData(data);
        setOriginalVersion(data);
      } else {
        const defaultData = {
          x: 1, y: 0, z: 0,
          storeUrl: platform === 'ios' ? 'https://apps.apple.com/app/id6781981681' : 'https://play.google.com/store/apps/details?id=com.kupigames.tower',
          forceUpdate: false
        };
        setVersionData(defaultData);
        setOriginalVersion(null);
        setMessage({ type: 'error', text: `Version document for ${platform.toUpperCase()} not found. Using defaults.` });
      }
    } catch (error) {
      console.error('Error fetching version:', error);
      setMessage({ type: 'error', text: 'Failed to fetch current version from Firestore' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Validation
      if (versionData.x < 0 || versionData.y < 0 || versionData.z < 0) {
        setMessage({ type: 'error', text: 'Version numbers cannot be negative' });
        return;
      }

      if (!versionData.storeUrl || !versionData.storeUrl.startsWith('http')) {
        setMessage({ type: 'error', text: 'Please provide a valid store URL' });
        return;
      }

      const docName = platform === 'ios' ? 'currentVersion_ios' : 'currentVersion';
      const docRef = doc(db, 'settings', docName);
      await setDoc(docRef, versionData);

      setOriginalVersion(versionData);
      setMessage({ type: 'success', text: 'Version updated successfully! Users will be prompted to update.' });
    } catch (error) {
      console.error('Error saving version:', error);
      setMessage({ type: 'error', text: 'Failed to save version to Firestore' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!originalVersion) return true;
    return JSON.stringify(versionData) !== JSON.stringify(originalVersion);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: '#6b7280' }}>Loading version data...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Smartphone size={28} style={{ color: '#3b82f6' }} />
          <h3 style={{ margin: 0 }}>Version Control</h3>
        </div>
        <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
          Manage the minimum required app version. Users with older versions will be prompted to update.
        </p>
      </div>

      {/* Platform Toggle */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setPlatform('android')}
          style={{
            flex: 1, padding: '0.75rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
            background: platform === 'android' ? '#10b981' : '#f3f4f6',
            color: platform === 'android' ? 'white' : '#4b5563',
            border: platform === 'android' ? 'none' : '1px solid #d1d5db'
          }}
        >
          Android
        </button>
        <button
          onClick={() => setPlatform('ios')}
          style={{
            flex: 1, padding: '0.75rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
            background: platform === 'ios' ? '#3b82f6' : '#f3f4f6',
            color: platform === 'ios' ? 'white' : '#4b5563',
            border: platform === 'ios' ? 'none' : '1px solid #d1d5db'
          }}
        >
          iOS (Apple)
        </button>
      </div>

      {/* Current Version Display */}
      {originalVersion && (
        <div style={{
          background: '#f3f4f6',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>Current Version in Firestore:</p>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
            {originalVersion.x}.{originalVersion.y}.{originalVersion.z}
          </p>
        </div>
      )}

      {/* Version Input Form */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Version Number (x.y.z)</h4>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Major (x)
            </label>
            <input
              type="number"
              min="0"
              value={versionData.x}
              onChange={(e) => setVersionData({ ...versionData, x: parseInt(e.target.value) || 0 })}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Minor (y)
            </label>
            <input
              type="number"
              min="0"
              value={versionData.y}
              onChange={(e) => setVersionData({ ...versionData, y: parseInt(e.target.value) || 0 })}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Patch (z)
            </label>
            <input
              type="number"
              min="0"
              value={versionData.z}
              onChange={(e) => setVersionData({ ...versionData, z: parseInt(e.target.value) || 0 })}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            Store URL
          </label>
          <input
            type="text"
            value={versionData.storeUrl}
            onChange={(e) => setVersionData({ ...versionData, storeUrl: e.target.value })}
            placeholder="https://play.google.com/store/apps/details?id=..."
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={versionData.forceUpdate}
              onChange={(e) => setVersionData({ ...versionData, forceUpdate: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>
              Force Update (block app usage until user updates)
            </span>
          </label>
        </div>

        {/* Preview */}
        <div style={{
          background: '#f9fafb',
          padding: '1rem',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>Preview:</p>
          <p style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>
            Version <strong>{versionData.x}.{versionData.y}.{versionData.z}</strong>
            {versionData.forceUpdate && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#ef4444' }}>
                (Force Update)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          marginBottom: '1rem',
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#991b1b',
          borderRadius: '8px',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving || !hasChanges()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          background: hasChanges() ? '#3b82f6' : '#9ca3af',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: hasChanges() ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          fontWeight: '600',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => {
          if (hasChanges() && !saving) {
            e.currentTarget.style.background = '#2563eb';
          }
        }}
        onMouseLeave={(e) => {
          if (hasChanges()) {
            e.currentTarget.style.background = '#3b82f6';
          }
        }}
      >
        <Save size={20} />
        {saving ? 'Saving...' : 'Save Version'}
      </button>

      {/* Warning */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
          <AlertCircle size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#92400e', fontSize: '0.875rem' }}>
              Important
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#78350f' }}>
              When you increase the version number, all users with older versions will be prompted to update their app.
              Make sure the new version is available in the store before updating this value.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionManager;
